import React, { useState } from 'react';
import {
  Box, TextField, Button, Typography, CircularProgress, Paper,
  FormControl, InputLabel, Select, MenuItem, Stack
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../store/slices/alertSlice';

import {useCreateCoupon,useCouponDetails,useUpdateCoupon} from './coupons.hooks';
import { useNavigate } from 'react-router-dom';
import { constants } from '../../constants';
import { useParams } from 'react-router-dom';
export default function AddCoupon() {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { data: couponData, isLoading: isLoadingCoupon,error:coupenLoadError } = useCouponDetails(id);
  
  React.useEffect(() => {
    if (coupenLoadError) {
      dispatch(showAlert({
        severity: 'error',
        title: 'Failed to load coupon details. Please try again.'
      }));
      navigate('/coupons/manage');
    }
  }, [coupenLoadError, dispatch]);


  const [formData, setFormData] = useState({
    code: '',
    type: constants.COUPEN_TYPE.FIXED,
    value:null,
    start_date:null,
    end_date:null,
    minimum_purchase_value:null,
    status:constants.COUPEN_STATUS.BLOCKED
  });


  React.useEffect(() => {
    if (isEditMode && couponData) {
      setFormData({
        code: couponData.code,
        type: couponData.type,
        value: couponData.value,
        start_date: couponData.start_date,
        end_date: couponData.end_date,
        minimum_purchase_value: couponData.minimum_purchase_value,
        status: couponData.status
      });
    }
  }, [isEditMode, couponData]);

  const {
    mutate:createCoupon,
    isLoading: isCreating,
  } = useCreateCoupon(
    () => {
      dispatch(showAlert({ severity: 'success', title: 'Coupon created successfully!' }));
      navigate('/coupons/manage');
    },
    (error) => {
      dispatch(showAlert({
        severity: 'error',
        title: error?.response?.data?.message || 'Failed to create coupon'
      }));
    }
  );


  const {
    mutate: updateCoupon,
    isLoading: isUpdating,
  } = useUpdateCoupon(
    () => {
      dispatch(showAlert({ severity: 'success', title: 'Coupon updated successfully!' }));
      navigate('/coupons/manage'); // Redirect after update
    },
    (error) => {
      dispatch(showAlert({ severity: 'error', title: error?.response?.data?.message || 'Failed to update coupon' }));
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isEditMode) {
      updateCoupon({ id, ...formData });
    } else {
      createCoupon(formData); // ✅ Trigger create API
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 500, mx: 'auto', mt: 6 }}>
    <Typography variant="h5" gutterBottom>{isEditMode ? 'Update Coupon' : 'Create Coupon'}</Typography>
    {isCreating && <CircularProgress />}
    {(isEditMode && isLoadingCoupon) && <CircularProgress />}


      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={3}>


          <FormControl fullWidth required>
          <TextField
            label="Coupon Code"
            name="code"
            type="text"
            value={formData.code}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />

          </FormControl>


          <FormControl fullWidth required>
          <InputLabel id="type-label">Coupon Type</InputLabel>
            <Select
              labelId="type-label"
              name="type"
              value={formData.type}
              label="Select Coupon Type"
              onChange={handleChange}
            >
            
                <MenuItem key={constants.COUPEN_TYPE.FIXED} value={constants.COUPEN_TYPE.FIXED}>
                  {'Fixed'}
                </MenuItem>
                <MenuItem key={constants.COUPEN_TYPE.PERCENTAGE} value={constants.COUPEN_TYPE.PERCENTAGE}>
                  {'Percentage'}
                </MenuItem>
  
            </Select>

          </FormControl>
          
          <FormControl fullWidth required>
          <TextField
            label="Coupon Value"
            name="value"
            type="number"
            value={ formData.value && Number(formData.value) % 1 === 0
            ? Number(formData.value).toFixed(0) // remove .00
            : formData.value }
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />
         </FormControl>

         <FormControl fullWidth required>
         <TextField
            label="Start Date"
            name="start_date"
            type="date"
            value={formData.start_date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />
      </FormControl>
      <FormControl fullWidth required>
         <TextField
            label="End Date"
            name="end_date"
            type="date"
            value={formData.end_date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />
      </FormControl>

      <FormControl fullWidth required>
          <TextField
            label="Minimum Purchase Amount"
            name="minimum_purchase_value"
            type="number"
            value={formData.minimum_purchase_value}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />
         </FormControl>

         <FormControl fullWidth required>
          <InputLabel id="coupon-status-label">Coupon Status</InputLabel>
            <Select
              labelId="coupon-status-label"
              name="status"
              value={formData.status}
              label="Select Coupon Status"
              onChange={handleChange}
            >
            
                <MenuItem key={constants.COUPEN_STATUS.BLOCKED} value={constants.COUPEN_STATUS.BLOCKED}>
                  {'Blocked'}
                </MenuItem>
                <MenuItem key={constants.COUPEN_STATUS.ACTIVE} value={constants.COUPEN_STATUS.ACTIVE}>
                  {'Active'}
                </MenuItem>
  
            </Select>

          </FormControl>

          <Button
  type="submit"
  variant="contained"
  fullWidth
  disabled={isCreating || isUpdating || isLoadingCoupon}
>
  {isCreating || isUpdating ? <CircularProgress size={24} /> : isEditMode ? 'Update Coupon' : 'Create Coupon'}
</Button>
        </Stack>
      </Box>
    </Paper>
  );
}
